import { Fragment, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';

import { listarPagos, filtrarPagos, limpiarPagos } from '../../../actions/pagosProyectos';

import NuevoPagoModal from './modals/NuevoPagoModal';
import EditarPagoModal from './modals/EditarPagoModal';
import EliminarPagoModal from './modals/EliminarPagoModal';

const formatDate = (value) => {
    if (!value) return 'Sin definir';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString('es-MX');
};

const formatMoney = (n) => {
    const v = Number(n || 0);
    return `$${new Intl.NumberFormat('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(v)}`;
};

const PagosProyecto = ({ idProyecto }) => {
    const dispatch = useDispatch();

    const { filtrado } = useSelector(state => state.pagosProyectos);

    const [textoFiltro, setTextoFiltro] = useState('');
    const [modalNuevo, setModalNuevo] = useState(false);
    const [pagoEditar, setPagoEditar] = useState(null);
    const [pagoEliminar, setPagoEliminar] = useState(null);

    useEffect(() => {
        if (!idProyecto) return;

        dispatch(listarPagos({ id_proyecto: idProyecto }));

        return () => {
            dispatch(limpiarPagos());
        };
    }, [dispatch, idProyecto]);

    const onChangeFiltroTabla = (e) => {
        const val = e.target.value;
        setTextoFiltro(val);
        dispatch(filtrarPagos(val));
    };

    const columns = useMemo(() => ([
        {
            name: 'Fecha',
            selector: row => formatDate(row?.fecha),
            sortable: true,
            wrap: true,
            grow: 1
        },
        {
            name: 'Monto',
            selector: row => row?.monto || 0,
            sortable: true,
            right: true,
            cell: row => (
                <div style={{ width: '100%', textAlign: 'right' }}>
                    {formatMoney(row?.monto)}
                </div>
            ),
            grow: 1
        },
        {
            name: 'Método de pago',
            selector: row => row?.metodo_pago?.nombre || 'Sin definir',
            sortable: true,
            wrap: true,
            grow: 1
        },
        {
            name: 'Referencia',
            selector: row => row?.referencia || '—',
            sortable: true,
            wrap: true,
            grow: 1
        },
        {
            name: 'Responsable',
            selector: row => `${row?.responsable?.nombre || ''} ${row?.responsable?.apellido || ''}`.trim() || 'Sin definir',
            sortable: true,
            wrap: true,
            grow: 1
        },
        {
            name: 'Opciones',
            width: '120px',
            cell: row => (
                <div className="d-flex align-items-center gap-2">
                    <button
                        type="button"
                        className="btn btn-sm btn-link px-2"
                        title="Editar"
                        onClick={() => setPagoEditar(row)}
                    >
                        <i className="fa fa-edit text-warning" />
                    </button>

                    <button
                        type="button"
                        className="btn btn-sm btn-link px-2"
                        title="Eliminar"
                        onClick={() => setPagoEliminar(row)}
                    >
                        <i className="fas fa-trash text-danger" />
                    </button>
                </div>
            )
        }
    ]), []);

    const paginationOptions = {
        rowsPerPageText: 'Filas por página',
        rangeSeparatorText: 'de',
        selectAllRowsItem: true,
        selectAllRowsItemText: 'Todos'
    };

    return (
        <Fragment>
            <div className="d-flex justify-content-end mb-3">
                <div className="d-flex align-items-center gap-2 w-100 justify-content-end">
                    <div style={{ minWidth: 280 }}>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Buscar en la tabla..."
                            value={textoFiltro}
                            onChange={onChangeFiltroTabla}
                        />
                    </div>

                    <button type="button" className="btn btn-primary" onClick={() => setModalNuevo(true)}>
                        <i className="feather icon-plus" /> Registrar pago
                    </button>
                </div>
            </div>

            <div className="mt-2">
                <DataTable
                    columns={columns}
                    data={filtrado || []}
                    pagination
                    paginationComponentOptions={paginationOptions}
                    highlightOnHover
                    striped
                    responsive
                    persistTableHead
                    noDataComponent="Sin pagos registrados"
                />
            </div>

            <NuevoPagoModal
                show={modalNuevo}
                idProyecto={idProyecto}
                onHide={() => setModalNuevo(false)}
                onSuccess={() => setModalNuevo(false)}
            />

            <EditarPagoModal
                show={!!pagoEditar}
                idProyecto={idProyecto}
                pago={pagoEditar}
                onHide={() => setPagoEditar(null)}
                onSuccess={() => setPagoEditar(null)}
            />

            <EliminarPagoModal
                show={!!pagoEliminar}
                idProyecto={idProyecto}
                idPago={pagoEliminar?._id || null}
                onHide={() => setPagoEliminar(null)}
            />
        </Fragment>
    );
};

export default PagosProyecto;
